import { log } from "../../engine/logger/logger";
import { openModal } from "../../ui/containers/Modal";
import { createElement } from "../../ui/ui";
import { StaggeredParticle } from "../anims/particles/StaggeredParticle";
import { ParticleManager } from "../anims/particleSystem";
import Colors from "../Colors";
import { gameData } from "../GameManager";
import { diedMsg, staggeredMsg } from "../messages/AttackMessages";
import { DeathModal } from "../ui/endConds/death";
import { Actor, ActorBreed, WieldMode } from "./Actor";
import {
  genericController,
  surroundController,
} from "./controllers/genericController";
import { canXPOnFloor, hasLevelUp, XPForDepth } from "./xpLevels";

const standardTriggers = {
  health: (actor: Actor) => {
    diedMsg(actor);
    actor.end();
    gameData.actors.remove(actor.id);
    if (canXPOnFloor(gameData.xp, gameData.current.depth)) {
      gameData.xp += XPForDepth(actor.level || 0, gameData.current.depth);
      if (hasLevelUp(gameData.player.level, gameData.xp)) {
        log(
          `%c{${Colors.RED_4}}Level Up%c{} Press %c{${Colors.RED_4}}x%c{} to level.`
        );
      } else {
        log(
          `%c{${Colors.B_GREY_4}}You gain %c{${Colors.RED_4}}${actor.level}%c{${Colors.B_GREY_4}}xp for the victory`
        );
      }
    } else {
      log(`You feel nothing, delve deeper for more XP.`);
    }
  },
  poise: (actor: Actor) => {
    if (actor.alive) {
      staggeredMsg(actor);
      actor.status.staggeredCount = 0;
      actor.status.staggered = true;
    }
  },
};

const breeds = {
  player: {
    displayPriority: 100,
    glyph: {
      fg: Colors.B_GREY_5,
      bg: "transparent",
      ch: "@",
    },
    baseStats: {
      level: 1,
      health: 100,
      stamina: 100,
      poise: 10,
      force: 1,
      strength: 1,
      weight: 20,
    },
    triggers: {
      health: (actor: Actor) => {
        diedMsg(actor);
        actor.end();
        openModal(createElement(DeathModal, { x: 0, y: 0, w: 32, h: 16 }, {}));
      },
      poise: (actor: Actor) => {
        staggeredMsg(actor);
        actor.addParticle(
          ParticleManager.makeUntilStartOfTurn(
            StaggeredParticle,
            actor.x,
            actor.y,
            actor.breed.fg
          )
        );
        actor.status.staggeredCount = 0;
        actor.status.staggered = true;
      },
    },
    text: {
      name: "A Pilgrim (You)",
      description: "You've journeyed far to get here. But why have you come?",
      logName: "You",
    },
    wielding: { left: "spade", right: "bLid", mode: WieldMode.oneHanded },
    wearing: { armor: "tunic" },
    contains: [["flask", 1]],
  },
  faithless: {
    displayPriority: 100,
    glyph: {
      fg: Colors.B_GREY_4,
      bg: "transparent",
      ch: "F",
    },
    text: {
      name: "Faithless",
      description:
        "Those left empty without a cause to live for. They keep to the shadows, but are quick to strike when the chance arises.",
      logName: "Faithless",
    },
    baseStats: {
      level: 1,
      health: 30,
      stamina: 100,
      poise: 15,
    },
    baseAttack: {
      stamina: 50,
      poise: 10,
      damage: 16,
    },
    triggers: standardTriggers,
    actionController: surroundController,
  },
  heretic: {
    displayPriority: 100,
    glyph: {
      fg: Colors.B_GREY_4,
      bg: "transparent",
      ch: "H",
    },
    text: {
      name: "Heretic",
      description:
        "Outcast long ago heretics seek only vengence from those that wronged them.",
      logName: "Heretic",
    },
    baseStats: {
      level: 2,
      health: 45,
      stamina: 100,
      poise: 20,
    },
    baseAttack: {
      stamina: 50,
      poise: 14,
      damage: 18,
    },
    triggers: standardTriggers,
    actionController: genericController,
  },
  defiler: {
    displayPriority: 100,
    glyph: {
      fg: Colors.B_GREY_4,
      bg: "transparent",
      ch: "D",
    },
    text: {
      name: "Defiler",
      description:
        "They have betrayed their cause for their own ends. Carved out a piece of their soul for some coin in their purse.",
      logName: "Defiler",
    },
    baseStats: {
      level: 4,
      health: 60,
      stamina: 100,
      poise: 40,
    },
    baseAttack: {
      stamina: 30,
      poise: 20,
      damage: 20,
    },
    triggers: standardTriggers,
    actionController: surroundController,
  },
  apostate: {
    displayPriority: 100,
    glyph: {
      fg: Colors.B_GREY_4,
      bg: "transparent",
      ch: "A",
    },
    text: {
      name: "Apostate",
      description:
        "They've turned their back on their cause in exchange for great power.",
      logName: "Apostate",
    },
    baseStats: {
      level: 4,
      health: 80,
      stamina: 100,
      poise: 40,
    },
    baseAttack: {
      stamina: 30,
      poise: 15,
      damage: 25,
    },
    triggers: standardTriggers,
    actionController: genericController,
  },
  cultist: {
    displayPriority: 100,
    glyph: {
      fg: Colors.B_GREY_4,
      bg: "transparent",
      ch: "c",
    },
    text: {
      name: "Cultist",
      description:
        "A true believer of a false cause. Unwilling to act alone, but quick to defend their leaders.",
      logName: "Cultist",
    },
    baseStats: {
      level: 2,
      health: 40,
      stamina: 100,
      poise: 30,
    },
    baseAttack: {
      stamina: 60,
      poise: 5,
      damage: 30,
    },
    triggers: standardTriggers,
    actionController: surroundController,
  },
  falseProphet: {
    displayPriority: 100,
    glyph: {
      fg: Colors.B_GREY_4,
      bg: "transparent",
      ch: "P",
    },
    text: {
      name: "False Prophet",
      description:
        "There are many ignobile causes in the world and each has their prophet.",
      logName: "False Prophet",
    },
    baseStats: {
      level: 1,
      health: 80,
      stamina: 100,
      poise: 50,
    },
    baseAttack: {
      stamina: 40,
      poise: 15,
      damage: 25,
    },
    triggers: standardTriggers,
    actionController: surroundController,
  },
} as const;

export const BreedTypes = Object.entries(breeds).reduce((r, [key, value]) => {
  r[key as keyof typeof breeds] = new ActorBreed(key, value);
  return r;
}, {} as Record<keyof typeof breeds, ActorBreed>);
